import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { Image, Upload, X, Sparkles, Download, Copy, Square, Smartphone, Monitor, RectangleHorizontal, Palette, Zap, Loader2, Clock } from 'lucide-react';
import { PageTooltip } from '../components/TooltipSystem';
import { useBranding } from '../../context/BrandingContext';

const ASPECT_RATIOS = [
  { value: '1:1', label: 'Quadrato', sub: 'Instagram Post', icon: Square },
  { value: '9:16', label: 'Verticale', sub: 'Story / Status', icon: Smartphone },
  { value: '4:5', label: 'Feed', sub: 'Instagram Feed', icon: RectangleHorizontal },
  { value: '16:9', label: 'Orizzontale', sub: 'Cover / Banner', icon: Monitor },
];

export default function AdminImageBuilder() {
  const branding = useBranding();
  const [prompt, setPrompt] = useState('');
  const [references, setReferences] = useState([]); // {id, preview, base64}
  const [aspect, setAspect] = useState('1:1');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // {data, mime_type}
  const [resultMessage, setResultMessage] = useState('');
  const fileRef = useRef(null);

  const handleAddRef = (e) => {
    const files = Array.from(e.target.files || []);
    if (references.length + files.length > 4) { toast.error('Massimo 4 immagini di riferimento'); return; }

    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} troppo grande (max 10MB)`); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = reader.result.split(',')[1];
        setReferences(prev => [...prev, {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          preview: reader.result,
          base64: b64,
          name: file.name,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeRef = (id) => setReferences(prev => prev.filter(r => r.id !== id));

  const insertTag = (idx) => {
    setPrompt(p => p + ` [ref${idx + 1}]`);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Scrivi un prompt'); return; }
    setGenerating(true);
    setResult(null);
    setResultMessage('');
    try {
      const res = await adminApi.post('/admin/image-builder/generate', {
        prompt,
        reference_images: references.map(r => r.base64),
        aspect_ratio: aspect,
      }, { timeout: 120000 });

      if (res.data.success) {
        setResult(res.data.image);
        setResultMessage(res.data.message || '');
        toast.success('Immagine generata!');
      } else {
        toast.error(res.data.message || 'Generazione fallita');
        setResultMessage(res.data.message || '');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const ext = result.mime_type?.includes('jpeg') ? 'jpg' : 'png';
    const link = document.createElement('a');
    link.href = `data:${result.mime_type};base64,${result.data}`;
    link.download = `lazo-image-${Date.now()}.${ext}`;
    link.click();
  };

  const handleCopyBase64 = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.data.slice(0, 100) + '...');
    toast.success('Anteprima copiata');
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-6xl" data-testid="image-builder-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A] flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#D44E20] to-[#F59E0B] flex items-center justify-center">
            <Sparkles size={20} className="text-white" />
          </div>
          Image Builder
        </h1>
        <p className="text-[#64748B] text-sm mt-1">Crea immagini professionali per social e WhatsApp con Nano Banana AI</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Form */}
        <div className="lg:col-span-3 space-y-5">
          {/* Reference images */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#1A1A1A] flex items-center gap-2">
                <Image size={15} className="text-[#D44E20]" /> Immagini di riferimento
              </h3>
              <span className="text-[10px] text-[#94A3B8]">{references.length}/4</span>
            </div>

            <div className="flex gap-3 flex-wrap mb-3">
              {references.map((ref, idx) => (
                <div key={ref.id} className="relative group" data-testid={`ref-image-${idx}`}>
                  <img src={ref.preview} alt="" className="w-20 h-20 rounded-xl object-cover border-2 border-slate-100 group-hover:border-[#D44E20]/40 transition-colors" />
                  <button onClick={() => removeRef(ref.id)} className="absolute -top-2 -right-2 w-5 h-5 bg-[#EF4444] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={10} />
                  </button>
                  <button onClick={() => insertTag(idx)} className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-[#1A1A1A] text-white text-[9px] px-1.5 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    [ref{idx + 1}]
                  </button>
                </div>
              ))}

              {references.length < 4 && (
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="add-ref-btn"
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 hover:border-[#D44E20]/50 flex flex-col items-center justify-center gap-1 text-[#94A3B8] hover:text-[#D44E20] transition-colors"
                >
                  <Upload size={16} />
                  <span className="text-[9px]">Aggiungi</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleAddRef} />
            <p className="text-[10px] text-[#94A3B8]">Carica logo, foto del locale o prodotti come riferimento. Hover per inserire il tag [ref] nel prompt.</p>
          </div>

          {/* Prompt */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            <label className="text-sm font-semibold text-[#1A1A1A] mb-2 block">Prompt</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              data-testid="image-prompt"
              rows={4}
              placeholder="Es: Crea un post Instagram per promuovere il Taco Tuesday. Usa [ref1] come logo in alto a destra. Testo grande: 'TACO TUESDAY' con sfondo messicano colorato. Aggiungi '2 tacos a 5€' in basso."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#D44E20]/20 focus:border-[#D44E20] transition-all"
            />
            {references.length > 0 && (
              <div className="flex gap-1.5 mt-2">
                <span className="text-[10px] text-[#94A3B8]">Inserisci:</span>
                {references.map((_, idx) => (
                  <button key={idx} onClick={() => insertTag(idx)} className="text-[10px] px-2 py-0.5 bg-[#FDECE7] text-[#D44E20] rounded-md hover:bg-[#D44E20] hover:text-white transition-colors font-medium">
                    [ref{idx + 1}]
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Aspect ratio */}
              <div>
                <label className="text-sm font-semibold text-[#1A1A1A] mb-2 block">Formato</label>
                <div className="grid grid-cols-2 gap-2" data-testid="aspect-selector">
                  {ASPECT_RATIOS.map(ar => {
                    const ArIcon = ar.icon;
                    return (
                      <button key={ar.value} onClick={() => setAspect(ar.value)} data-testid={`aspect-${ar.value}`}
                        className={`flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all ${
                          aspect === ar.value
                            ? 'border-[#D44E20] bg-[#FDECE7] text-[#D44E20]'
                            : 'border-slate-100 text-[#64748B] hover:border-slate-200'
                        }`}>
                        <ArIcon size={16} />
                        <div>
                          <PageTooltip page="image-builder" />
                          <p className="text-xs font-medium">{ar.label}</p>
                          <p className="text-[9px] opacity-70">{ar.sub}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Brand colors info (replaces Style table - colors come from Brand Identity) */}
              <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/60" data-testid="brand-colors-info">
                <label className="text-sm font-semibold text-[#1A1A1A] mb-2 block flex items-center gap-1.5">
                  <Palette size={13} /> Colori del brand
                </label>
                <p className="text-[11px] text-[#64748B] mb-2">L'AI utilizzerà i colori della tua Brand Identity. Per cambiarli vai su <b>Impostazioni → Brand</b>.</p>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: branding?.primary_color || '#D44E20' }} />
                    <span className="text-[11px] font-mono text-[#64748B]">{branding?.primary_color || '#D44E20'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
                    <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: branding?.secondary_color || '#a7e400' }} />
                    <span className="text-[11px] font-mono text-[#64748B]">{branding?.secondary_color || '#a7e400'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            data-testid="generate-btn"
            className="w-full py-3.5 bg-gradient-to-r from-[#D44E20] to-[#F59E0B] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2 text-sm"
          >
            {generating ? (
              <><Loader2 size={18} className="animate-spin" /> Generazione in corso...</>
            ) : (
              <><Zap size={18} /> Genera Immagine</>
            )}
          </button>
        </div>

        {/* Right: Preview */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] sticky top-6">
            <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
              <Palette size={15} className="text-[#D44E20]" /> Anteprima
            </h3>

            {generating && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-[#64748B]">Nano Banana sta creando...</p>
                <p className="text-[10px] text-[#94A3B8]">Può richiedere 15-30 secondi</p>
              </div>
            )}

            {!generating && !result && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center">
                  <Image size={28} className="text-slate-200" />
                </div>
                <p className="text-sm text-[#64748B]">L'immagine generata apparirà qui</p>
                <p className="text-[10px] text-[#94A3B8] max-w-[200px]">Scrivi un prompt, scegli formato e stile, poi clicca "Genera Immagine"</p>
              </div>
            )}

            {!generating && result && (
              <div>
                <div className="rounded-xl overflow-hidden border border-slate-100 mb-3">
                  <img
                    src={`data:${result.mime_type};base64,${result.data}`}
                    alt="Generated"
                    className="w-full"
                    data-testid="generated-image"
                  />
                </div>
                {resultMessage && (
                  <p className="text-xs text-[#64748B] mb-3 italic">{resultMessage}</p>
                )}
                <div className="flex gap-2">
                  <button onClick={handleDownload} data-testid="download-btn"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#D44E20] text-white rounded-xl text-sm font-medium hover:bg-[#B8421B] transition-colors">
                    <Download size={16} /> Scarica
                  </button>
                  <button onClick={() => { setResult(null); setResultMessage(''); }}
                    className="px-4 py-2.5 bg-slate-50 text-[#64748B] rounded-xl text-sm hover:bg-slate-100 transition-colors">
                    Nuova
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
